/**
 * ResidentTrendActions — V2 version of the Resident Trends & Actions widget.
 *
 * Shows actionable items grouped by 6 trigger types with severity indicators,
 * weekly sparklines, and acknowledge/dismiss actions.
 *
 * Props: campusId
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getAuthHeaders } from '../../../../services/auth';

const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

const getHeaders = () => getAuthHeaders();

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const TRIGGER_CONFIG = {
    volume_spike: { label: 'Volume Spike', icon: '📈', color: '#e74c3c', description: 'Significant increase in call volume this week vs. baseline' },
    sustained_increase: { label: 'Sustained Increase', icon: '📊', color: '#e67e22', description: 'Call volume rising for 3+ consecutive weeks' },
    high_frequency_new: { label: 'New High-Frequency', icon: '🔔', color: '#8e44ad', description: 'Previously low-volume residents now among highest callers' },
    nighttime_increase: { label: 'Nighttime Increase', icon: '🌙', color: '#2c3e50', description: 'Notable increase in calls between 10pm-6am' },
    reason_shift: { label: 'Reason Shift', icon: '🔄', color: '#2980b9', description: 'Significant change in call reason distribution' },
    new_reason: { label: 'New Reason', icon: '⚠️', color: '#16a085', description: 'Calling for reasons not in recent history' },
};

const TRIGGER_ORDER = ['volume_spike', 'sustained_increase', 'high_frequency_new', 'nighttime_increase', 'reason_shift', 'new_reason'];

const SEVERITY_CONFIG = {
    high: { label: 'High', cls: 'md-severity--high' },
    medium: { label: 'Medium', cls: 'md-severity--medium' },
    low: { label: 'Low', cls: 'md-severity--low' },
};

// ---------------------------------------------------------------------------
// Sparkline (mini bar chart)
// ---------------------------------------------------------------------------

const WeeklySparkline = ({ data, color }) => {
    if (!data || data.length === 0) return null;
    const max = Math.max(...data, 1);
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 24 }}>
            {data.map((v, i) => (
                <div key={i} style={{ width: 6, height: `${Math.max((v / max) * 100, 4)}%`, background: i === data.length - 1 ? color : `${color}60`, borderRadius: 2 }} />
            ))}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Metric Pill
// ---------------------------------------------------------------------------

const MetricPill = ({ label, value, sub }) => (
    <div style={{ background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)', padding: '6px 12px', display: 'inline-flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{label}</span>
        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-heading)' }}>{value}</span>
        {sub ? <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{sub}</span> : null}
    </div>
);

// ---------------------------------------------------------------------------
// Trigger-specific metrics
// ---------------------------------------------------------------------------

function getMetrics(item) {
    const sd = typeof item.supporting_data === 'string' ? JSON.parse(item.supporting_data) : item.supporting_data || {};
    switch (item.trigger_type) {
        case 'volume_spike':
            return [
                { label: 'This Week', value: sd.current_week_count ?? '—' },
                { label: 'Avg/Week', value: sd.baseline_week_avg ?? '—' },
                { label: '% Increase', value: sd.percent_increase != null ? `${sd.percent_increase}%` : '—' },
            ];
        case 'sustained_increase':
            return [
                { label: 'This Week', value: sd.week1_current ?? '—' },
                { label: 'Weeks Rising', value: sd.consecutive_weeks_increasing ?? '—' },
            ];
        case 'high_frequency_new':
            return [
                { label: 'This Week', value: sd.current_week_count ?? '—' },
                { label: 'Prior Avg', value: sd.baseline_week_avg ?? '—' },
                { label: 'Facility P75', value: sd.facility_p75_current ?? '—' },
            ];
        case 'nighttime_increase':
            return [
                { label: 'Night Calls', value: sd.night_current_count ?? '—' },
                { label: 'Prior Avg', value: sd.night_baseline_week_avg ?? '—' },
                { label: 'Night %', value: sd.night_current_pct != null ? `${Math.round(sd.night_current_pct)}%` : '—', sub: sd.night_baseline_pct != null ? `was ${Math.round(sd.night_baseline_pct)}%` : undefined },
            ];
        case 'reason_shift':
            return [
                { label: 'Reason', value: sd.reason_name ?? '—' },
                { label: 'Now', value: sd.current_pct != null ? `${Math.round(sd.current_pct)}%` : '—' },
                { label: 'Was', value: sd.baseline_pct != null ? `${Math.round(sd.baseline_pct)}%` : '—' },
            ];
        case 'new_reason':
            return [
                { label: 'Reason', value: sd.reason_name ?? '—' },
                { label: 'Calls', value: sd.current_count ?? '—' },
                { label: '% Total', value: sd.current_pct != null ? `${Math.round(sd.current_pct)}%` : '—' },
            ];
        default:
            return [];
    }
}

function getWeeklyData(item) {
    const sd = typeof item.supporting_data === 'string' ? JSON.parse(item.supporting_data) : item.supporting_data || {};
    if (sd.week1 !== undefined) return [sd.week5, sd.week4, sd.week3, sd.week2, sd.week1].filter(v => v !== undefined);
    if (sd.week1_current !== undefined) return [sd.week5, sd.week4, sd.week3, sd.week2, sd.week1_current].filter(v => v !== undefined);
    if (sd.night_week1 !== undefined) return [sd.night_week5, sd.night_week4, sd.night_week3, sd.night_week2, sd.night_week1].filter(v => v !== undefined);
    return [];
}

// ---------------------------------------------------------------------------
// ActionCard
// ---------------------------------------------------------------------------

const ActionCard = ({ item, onAcknowledge, onDismiss, isProcessing }) => {
    const [expanded, setExpanded] = useState(false);
    const severity = SEVERITY_CONFIG[item.severity] || SEVERITY_CONFIG.medium;
    const trigger = TRIGGER_CONFIG[item.trigger_type] || {};
    const metrics = getMetrics(item);
    const weeklyData = getWeeklyData(item);

    return (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-2)', overflow: 'hidden', background: 'var(--color-bg-surface)' }}>
            {/* Card header — clickable */}
            <div onClick={() => setExpanded(!expanded)} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: '10px 14px', cursor: 'pointer' }}>
                {/* Severity dot */}
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: SEVERITY_CONFIG[item.severity]?.cls === 'md-severity--high' ? '#e74c3c' : item.severity === 'medium' ? '#e67e22' : '#16a085', flexShrink: 0 }} />

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-heading)' }}>
                            {item.resident_first_name} {item.resident_last_name}
                        </span>
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{item.resident_room || ''}</span>
                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 500, color: severity.cls === 'md-severity--high' ? '#e74c3c' : item.severity === 'medium' ? '#e67e22' : '#16a085', textTransform: 'uppercase' }}>{severity.label}</span>
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.description || ''}
                    </div>
                </div>

                {/* Sparkline */}
                {weeklyData.length > 0 ? <WeeklySparkline data={weeklyData} color={trigger.color || '#2C7BE5'} /> : null}

                {/* Actions — disabled until backend API is ready */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                    {/*<button disabled={true} className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px', opacity: 0.4, cursor: 'not-allowed' }} title="Coming soon">Acknowledge</button>*/}
                    {/*<button disabled={true} className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px', opacity: 0.4, cursor: 'not-allowed', color: 'var(--color-text-muted)' }} title="Coming soon">Dismiss</button>*/}
                </div>

                {/* Expand arrow */}
                <span style={{ fontSize: 12, color: 'var(--color-text-muted)', transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0)' }}>▼</span>
            </div>

            {/* Expanded details */}
            {expanded ? (
                <div style={{ padding: '0 14px 12px', borderTop: '1px solid var(--color-border)' }}>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 'var(--space-3)' }}>
                        {metrics.map((m, i) => <MetricPill key={i} label={m.label} value={m.value} />)}
                    </div>
                    {item.description ? (
                        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-3)', lineHeight: 1.5 }}>{item.description}</p>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// TriggerSection
// ---------------------------------------------------------------------------

const TriggerSection = ({ triggerType, items, onAcknowledge, onDismiss, processingIds }) => {
    const [collapsed, setCollapsed] = useState(true);
    const config = TRIGGER_CONFIG[triggerType] || {};

    return (
        <div style={{ marginBottom: 'var(--space-4)' }}>
            <div onClick={() => setCollapsed(!collapsed)} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer', padding: 'var(--space-2) 0' }}>
                <span style={{ fontSize: 16 }}>{config.icon}</span>
                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-heading)' }}>{config.label}</span>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', background: 'var(--color-bg-tertiary)', padding: '1px 8px', borderRadius: 'var(--radius-full)' }}>{items.length}</span>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', flex: 1 }}>{config.description}</span>
                <span style={{ fontSize: 10, color: 'var(--color-text-muted)', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▼</span>
            </div>
            {!collapsed ? (
                <div>
                    {items.map((item) => (
                        <ActionCard key={item.action_id} item={item} onAcknowledge={onAcknowledge} onDismiss={onDismiss} isProcessing={processingIds.has(item.action_id)} />
                    ))}
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const ResidentTrendActionsV2 = ({ campusId }) => {
    const [data, setData] = useState({ action_items: [], summary_items: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingIds, setProcessingIds] = useState(new Set());

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
                method: 'POST',
                headers: await getHeaders(),
                body: JSON.stringify({
                    rep_type: 'residentactiontrends',
                    campus_id: campusId,
                    status: 'new',
                }),
            });
            const raw = await resp.json();
            const result = raw?.Status?.toLowerCase() === 'ok' ? raw.Res : raw?.Res || raw || {};
            setData(result && result.action_items ? result : { action_items: [], summary_items: [] });
        } catch (err) {
            setError('Failed to load action items');
        } finally {
            setLoading(false);
        }
    }, [campusId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleAcknowledge = useCallback((actionId) => {
        setProcessingIds((prev) => new Set(prev).add(actionId));
        // TODO: Replace with real API call when endpoint is ready
        setTimeout(() => {
            setData((prev) => ({ ...prev, action_items: prev.action_items.filter((i) => i.action_id !== actionId) }));
            setProcessingIds((prev) => { const n = new Set(prev); n.delete(actionId); return n; });
        }, 400);
    }, []);

    const handleDismiss = useCallback((actionId) => {
        setProcessingIds((prev) => new Set(prev).add(actionId));
        // TODO: Replace with real API call when endpoint is ready
        setTimeout(() => {
            setData((prev) => ({ ...prev, action_items: prev.action_items.filter((i) => i.action_id !== actionId) }));
            setProcessingIds((prev) => { const n = new Set(prev); n.delete(actionId); return n; });
        }, 400);
    }, []);

    const groupedItems = useMemo(() => {
        return TRIGGER_ORDER
            .map((type) => ({ type, items: data.action_items.filter((i) => i.trigger_type === type) }))
            .filter((g) => g.items.length > 0);
    }, [data]);

    const totalActive = data.action_items.length;
    const totalHigh = data.action_items.filter((i) => i.severity === 'high').length;

    return (
        <div className="md-section" style={{ overflow: 'hidden', padding: 0 }}>
            {/* Header */}
            <div style={{ background: '#1a8a7d', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ color: '#fff', fontSize: 'var(--font-size-base)', fontWeight: 600 }}>Resident Trends & Actions</span>
                    {totalActive > 0 ? (
                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, padding: '2px 10px', borderRadius: 10, background: totalHigh > 0 ? 'rgba(231,76,60,0.25)' : 'rgba(255,255,255,0.2)', color: '#fff' }}>
                            {totalActive} item{totalActive !== 1 ? 's' : ''}{totalHigh > 0 ? ` · ${totalHigh} high` : ''}
                        </span>
                    ) : null}
                </div>
                <button onClick={fetchData} disabled={loading} className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 14px', background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: '#fff' }}>
                    {loading ? 'Loading...' : 'Refresh'}
                </button>
            </div>

            {/* Content */}
            <div style={{ padding: '8px 20px 16px' }}>
                {loading && data.action_items.length === 0 ? (
                    <div style={{ padding: 'var(--space-8) 0', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>Loading action items...</div>
                ) : error ? (
                    <div style={{ padding: 'var(--space-8) 0', textAlign: 'center', color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
                        {error}
                        <div style={{ marginTop: 'var(--space-2)' }}><button onClick={fetchData} className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Retry</button></div>
                    </div>
                ) : groupedItems.length === 0 ? (
                    <div style={{ padding: 'var(--space-8) 0', textAlign: 'center' }}>
                        <div style={{ fontSize: 28, marginBottom: 'var(--space-2)' }}>✓</div>
                        <div style={{ color: 'var(--color-text-heading)', fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>No action items</div>
                        <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)', marginTop: 4 }}>All resident trends are within normal ranges</div>
                    </div>
                ) : (
                    groupedItems.map((group) => (
                        <TriggerSection key={group.type} triggerType={group.type} items={group.items} onAcknowledge={handleAcknowledge} onDismiss={handleDismiss} processingIds={processingIds} />
                    ))
                )}
            </div>
        </div>
    );
};

export default ResidentTrendActionsV2;
